/**
 * Canonical FAQ copy for AbleGo — used by FAQ page UI and FAQPage JSON-LD.
 * Edit here only; SEO structured data mirrors this content.
 */

export type FaqItem = {
  id: string;
  question: string;
  answer: string[];
  matchText?: string;
};

export type FaqCategory = {
  id: string;
  title: string;
  summary: string;
  items: FaqItem[];
};

export const FAQ_CATEGORIES: FaqCategory[] = [
  {
    id: 'booking-payments',
    title: 'Booking & payments',
    summary:
      'How to book, pay, and change your trip — designed for clarity before you travel.',
    items: [
      {
        id: 'how-to-book',
        question: 'How do I book a ride with AbleGo?',
        answer: [
          'Use our online booking flow to enter your pickup and drop-off, preferred time, and any accessibility or support needs. You can book as a guest with contact details, or sign in if you already have an account.',
          'After you submit, you should receive a confirmation by email with the key details of your request. If you need help at any step, contact us and we will talk you through it.',
        ],
        matchText: 'book online guest confirmation',
      },
      {
        id: 'payment-options',
        question: 'What payment methods do you accept?',
        answer: [
          'Where card checkout is available, you can pay securely online. We also support cash or bank transfer options where offered for your journey — the booking screen will show what applies to your trip.',
          'If you are unsure which option to choose, pick the one you are most comfortable with and add a note in your booking; our team can follow up if anything needs adjusting.',
        ],
        matchText: 'card stripe cash bank transfer pay',
      },
      {
        id: 'booking-confirmation',
        question: 'Will I get a confirmation after I book?',
        answer: [
          'Yes. You should receive an email confirmation that summarises your trip, contact details, and payment status where relevant. Keep that email handy in case you need to reference your booking with us.',
          'If you do not see it within a few minutes, check spam or promotions folders, then contact hello@ablego.co.uk with your name and pickup time so we can resend or verify the booking.',
        ],
        matchText: 'email confirmation receipt',
      },
      {
        id: 'changes-cancellations',
        question: 'Can I change or cancel my booking?',
        answer: [
          'We know plans change — especially around health appointments and mobility. Contact us as early as possible with your booking reference or the email address you used, and tell us what you need to adjust.',
          'Fair cancellation and change rules depend on how close it is to your pickup time and whether a vehicle or companion was already allocated. Our team will always try to offer a pragmatic solution.',
        ],
        matchText: 'cancel change amend modify refund',
      },
      {
        id: 'pricing-estimate',
        question: 'Is the fare on the booking page final?',
        answer: [
          'The fare shown during booking is an estimate based on the route and options you selected. Occasionally the final price may differ slightly if the route or waiting time changes significantly — where that happens we aim to be transparent before the trip concludes.',
          'If you need a fixed quote for budgeting or organisational approval, mention it when you book or speak to our team.',
        ],
        matchText: 'price cost fare estimate quote',
      },
    ],
  },
  {
    id: 'accessibility-passengers',
    title: 'Accessibility & passenger care',
    summary:
      'Inclusive transport built around dignity, predictable assistance, and the right vehicle for your mobility.',
    items: [
      {
        id: 'wheelchair-wav',
        question: 'Do you provide wheelchair-accessible vehicles (WAVs)?',
        answer: [
          'Yes. AbleGo is built around accessible transport. When you book, select the vehicle and access features you need so we can match you appropriately.',
          'If you use a specific type of wheelchair or seating system, add that in the special requirements field — it helps us confirm vehicle compatibility before the day.',
        ],
        matchText: 'wheelchair WAV accessible vehicle ramp lift',
      },
      {
        id: 'companion-assistance',
        question: 'Can someone travel with me to help during the journey?',
        answer: [
          'You can include details about a personal assistant or family member travelling with you in your booking notes. Many passengers prefer a familiar face alongside any professional support we arrange.',
          'If you need a trained AbleGo support worker for part or all of the trip, say so when you book (or ask us) and we will explain availability and how that works for your journey.',
        ],
        matchText: 'PA carer family companion escort',
      },
      {
        id: 'medical-mobility',
        question: 'I have complex mobility or medical needs — can AbleGo still help?',
        answer: [
          'We regularly support passengers with a range of health conditions, frailty, sensory disabilities, and neurodivergence. The more specific you can be about what helps you feel safe (pace, communication style, equipment), the better we can prepare.',
          'We are not an emergency ambulance service. For urgent medical emergencies you should use NHS 999 services. For planned healthcare transport, book in advance where possible.',
        ],
        matchText: 'disability health frail elderly dementia autism',
      },
      {
        id: 'assistance-dogs',
        question: 'Are assistance dogs welcome?',
        answer: [
          'Assistance dogs recognised under UK equality law are welcome, subject to vehicle capacity and safety. Please tell us when you book so we reserve appropriate space and brief the driver.',
        ],
        matchText: 'guide dog hearing dog allergy',
      },
      {
        id: 'equipment-luggage',
        question: 'What about mobility equipment, luggage, or shopping?',
        answer: [
          'Mention scooters, walkers, oxygen, or extra bags when you book. That allows us to allow time for loading and to choose a vehicle with suitable storage.',
          'If anything is unusually heavy or needs two-person handling, flag it upfront so we can plan safe manual handling.',
        ],
        matchText: 'scooter hoist oxygen bags luggage',
      },
    ],
  },
  {
    id: 'companions-professionals',
    title: 'Support workers & professional companions',
    summary:
      'Understanding how AbleGo complements human support — not replaces it.',
    items: [
      {
        id: 'what-support-worker-does',
        question: 'What does an AbleGo support worker do?',
        answer: [
          'Where arranged, support workers bring a safeguarding-first mindset and practical help — from reassurance at the door through to accompaniment during appointments or errands, according to what you have booked.',
          'They are not a substitute for clinical care or social care packages commissioned by local authorities, but they can make door-to-door transport feel workable and dignified.',
        ],
        matchText: 'chaperone escort safeguarding',
      },
      {
        id: 'matching-needs',
        question: 'How do you match support to my needs?',
        answer: [
          'We use the information you provide at booking — including communication preferences, mobility, and behavioural support needs — alongside our operational experience to allocate appropriately.',
          'If something changes between booking and pickup, tell us straight away so we can brief the crew.',
        ],
        matchText: 'match allocate brief',
      },
      {
        id: 'safeguarding-adults',
        question: 'How do you approach safeguarding?',
        answer: [
          'Safeguarding is central to AbleGo training and onboarding. Drivers and support workers operating under our brand are expected to follow clear policies on boundaries, reporting concerns, and respectful communication.',
          'If you ever feel unsafe, contact us straight away — and use emergency services where there is immediate risk.',
        ],
        matchText: 'safe vulnerable adult child policy',
      },
      {
        id: 'become-support-worker',
        question: 'How do I join as a support worker?',
        answer: [
          'Start with our support worker intake form on our website. We gather your experience and contact details first; a short video validation call follows before onboarding and safeguarding checks.',
          'There is no self-service account login until after we have verified you — we take a deliberate human-led approach.',
        ],
        matchText: 'apply register recruitment DBS jobs',
      },
    ],
  },
  {
    id: 'drivers-partners',
    title: 'Drivers & vehicle partners',
    summary:
      'For people who want to supply accessible capacity with AbleGo-aligned standards.',
    items: [
      {
        id: 'become-driver',
        question: 'How do I register as a driver or vehicle partner?',
        answer: [
          'Complete the driver intake form with your details and vehicle information. We then schedule a video call to validate your submission and explain insurance, safeguarding, and platform expectations.',
          'Onboarding stays intentionally human-led — we prioritise trustworthy supply over instant self-approval.',
        ],
        matchText: 'register franchise partner supply',
      },
      {
        id: 'vehicle-standards',
        question: 'What vehicle standards apply?',
        answer: [
          'We expect legally roadworthy vehicles, suitable insurance for the service you deliver, and honest disclosure of adaptations (wheelchair capacity, ramps, anchors). Exact checks are confirmed during onboarding.',
        ],
        matchText: 'insurance MOT compliance',
      },
      {
        id: 'payments-drivers',
        question: 'How are drivers paid?',
        answer: [
          'Commercial arrangements evolve as we scale. During onboarding we explain how trips are priced, how statements work, and any platform fees that apply. Ask us for the latest commercial summary if you are evaluating partnership.',
        ],
        matchText: 'earnings payout stripe connect',
      },
      {
        id: 'video-validation',
        question: 'Why is there a video call for applications?',
        answer: [
          'We use short video validation to reduce fraud, protect passengers, and set expectations early. It is quicker for many applicants than posting paper packs, and it builds trust before anyone handles live bookings.',
        ],
        matchText: 'interview verify identity',
      },
    ],
  },
  {
    id: 'coverage-timing',
    title: 'Coverage, hours & planning',
    summary:
      'Setting expectations for when and where we can serve you best.',
    items: [
      {
        id: 'where-we-operate',
        question: 'Where does AbleGo operate?',
        answer: [
          'AbleGo is building UK-wide accessible transport with a strong focus on compassionate service. Exact same-day availability depends on where you are and fleet density in that area.',
          'When you book, enter your real addresses — the system and our team will tell you if we need to suggest an alternative time or route.',
        ],
        matchText: 'UK nationwide area region local',
      },
      {
        id: 'advance-booking',
        question: 'How far ahead should I book?',
        answer: [
          'For healthcare, school, or recurring trips, booking several days ahead usually gives the best choice of time and vehicle type. For urgent same-day needs, try the booking flow first, then call us if you are stuck.',
        ],
        matchText: 'same day advance notice lead time',
      },
      {
        id: 'no-show-waiting',
        question: 'What if I am late or not at the pickup point?',
        answer: [
          'Drivers and companions will try to reach you on the phone number provided. Repeated no-shows can affect future bookings; if you are delayed, let us know as soon as you can so we can keep others on schedule.',
        ],
        matchText: 'late waiting missed pickup',
      },
    ],
  },
  {
    id: 'safety-trust',
    title: 'Safety, insurance & trust',
    summary:
      'How we think about risk, transparency, and continuous improvement.',
    items: [
      {
        id: 'driver-checks',
        question: 'How are drivers and partners vetted?',
        answer: [
          'We use a combination of onboarding checks, documentary evidence, and video validation suited to role. Policies are tightened as our operating model matures.',
          'Tell us promptly if anything about your journey feels unsafe or unprofessional.',
        ],
        matchText: 'DBS vetting licence check background',
      },
      {
        id: 'insurance-cover',
        question: 'Is insurance included?',
        answer: [
          'Commercial insurance requirements depend on vehicle ownership and how the trip is operated. During booking and onboarding we clarify what cover applies — never assume grey-area ride-share cover for WAV work.',
          'Organisations commissioning transport should speak to us for a summary suitable for procurement.',
        ],
        matchText: 'policy liability claim indemnity',
      },
      {
        id: 'complaints',
        question: 'How do I make a complaint or give praise?',
        answer: [
          'Email hello@ablego.co.uk with your booking reference where possible. We log feedback, investigate proportionately, and learn from recurring themes.',
          'Positive shout-outs matter too — they help us recognise crews who embody AbleGo values.',
        ],
        matchText: 'complaint escalation feedback compliments',
      },
      {
        id: 'incidents-road',
        question: 'What happens if there is an incident during a journey?',
        answer: [
          'Health and safety always come first. In an emergency, use 999. For non-emergency incidents, contact us as soon as it is safe — we will guide you on documentation and next steps.',
        ],
        matchText: 'accident emergency breakdown',
      },
    ],
  },
  {
    id: 'privacy-account',
    title: 'Privacy, accounts & communication',
    summary:
      'Your data, your control — aligned with UK expectations on transparency.',
    items: [
      {
        id: 'personal-data',
        question: 'How do you use my personal data?',
        answer: [
          'We use contact and journey information to deliver the service, send confirmations, and improve operations. Our Privacy Policy describes categories of data, lawful bases, and retention in more detail.',
          'We do not sell personal data. Marketing is optional and separate from essential service emails.',
        ],
        matchText: 'GDPR ICO consent cookies',
      },
      {
        id: 'guest-vs-account',
        question: 'Do I need an account to book?',
        answer: [
          'Many journeys can be booked as a guest with an email and phone number. Accounts can make repeat bookings faster and help you see history where that feature is available.',
        ],
        matchText: 'login signup register',
      },
      {
        id: 'contact-response',
        question: 'How quickly do you respond to messages?',
        answer: [
          'We monitor hello@ablego.co.uk during business hours and aim to respond within one working day for routine queries. Urgent trip issues should be flagged by phone when a number is published for your booking.',
        ],
        matchText: 'support help desk SLA',
      },
    ],
  },
  {
    id: 'marketing-organisations',
    title: 'Schools, NHS & organisations',
    summary:
      'For commissioners and teams booking on behalf of others.',
    items: [
      {
        id: 'bulk-contract',
        question: 'Can schools or NHS teams book regular transport?',
        answer: [
          'Yes — speak to us about recurring patterns, invoicing, and safeguarding documentation. We can align with your internal governance and data-sharing agreements.',
        ],
        matchText: 'procurement contract invoice PO',
      },
      {
        id: 'best-practice-info',
        question: 'What information helps you quote accurately?',
        answer: [
          'Share typical routes, time windows, passenger needs summary, escort requirements, and cancellation patterns. The more realistic the picture, the better we can price and resource sustainably.',
        ],
        matchText: 'tender framework SEN transport',
      },
      {
        id: 'brand-values',
        question: 'What does “inclusive transport with compassion” mean in practice?',
        answer: [
          'It means designing journeys around the person: patient pacing, clear communication, accessible vehicles where needed, and respect for dignity. We want transport to feel like part of care, not an afterthought.',
        ],
        matchText: 'mission values brand',
      },
    ],
  },
];

export function flattenFaqForSchema(): { question: string; answerText: string }[] {
  const out: { question: string; answerText: string }[] = [];
  for (const cat of FAQ_CATEGORIES) {
    for (const item of cat.items) {
      out.push({
        question: item.question,
        answerText: item.answer.join('\n\n'),
      });
    }
  }
  return out;
}

export function allFaqSearchBlob(item: FaqItem, categoryTitle: string): string {
  return [
    categoryTitle,
    item.question,
    ...item.answer,
    item.matchText ?? '',
  ]
    .join(' ')
    .toLowerCase();
}
